/* global customElements */

/*
 * Righteous Guitars — PLEK Upsell
 * --------------------------------
 * Custom element rendered below the Add to Cart button on acoustic and
 * electric guitar product pages. Lets the customer opt into the $150
 * promo PLEK service, gated by a terms modal.
 *
 * Flow:
 *   1. Customer ticks checkbox -> box is force-unchecked, terms modal opens.
 *   2. Customer clicks Accept -> box is set checked, ATC button label
 *      changes to the "with PLEK" variant, acceptance is remembered for
 *      the rest of the page-load.
 *   3. Customer clicks ATC -> Symmetry's ProductForm submits the guitar
 *      (single-item /cart/add.js). On the 'on:cart:add' event that
 *      Symmetry dispatches after a successful add, this element fires a
 *      second /cart/add.js POST for the PLEK variant and refreshes the
 *      cart drawer / page header sections.
 *
 * Variant-change handling:
 *   The variant-picker dispatches 'on:variant:change' on the section.
 *   If the newly selected variant price >= threshold ($3,000 by default),
 *   the block hides itself and clears any pending PLEK intent -- the
 *   trust-badge snippet already advertises complimentary PLEK in that
 *   price band, so offering the paid add-on would be double-charging.
 *
 * Known gap:
 *   The sticky-atc snippet renders its own form outside <product-form>.
 *   Clicks on the sticky ATC bypass this element's on:cart:add listener.
 *   If we need PLEK to attach to sticky-atc submissions, that's a later
 *   iteration (wire on:cart:add on the sticky form or swap to a
 *   capture-phase submit listener on both forms).
 */

(function () {
  if (typeof customElements === 'undefined') return;
  if (customElements.get('rg-plek-upsell')) return;

  var DEFAULT_PLEK_VARIANT_ID = '7609288360034';
  var DEFAULT_CART_ADD_URL = '/cart/add.js';

  function resolveCartAddUrl() {
    if (window.theme && window.theme.routes && window.theme.routes.cartAdd) {
      return window.theme.routes.cartAdd;
    }
    return DEFAULT_CART_ADD_URL;
  }

  var RgPlekUpsell = function RgPlekUpsell() {
    return Reflect.construct(HTMLElement, [], RgPlekUpsell);
  };

  RgPlekUpsell.prototype = Object.create(HTMLElement.prototype);
  RgPlekUpsell.prototype.constructor = RgPlekUpsell;
  Object.setPrototypeOf(RgPlekUpsell, HTMLElement);

  RgPlekUpsell.prototype.connectedCallback = function () {
    if (this._inited) return;
    this._inited = true;

    this.plekVariantId = (this.dataset.plekVariantId || DEFAULT_PLEK_VARIANT_ID).trim();
    this.priceThreshold = parseInt(this.dataset.priceThreshold || '300000', 10);
    this.defaultAtcText = this.dataset.defaultAtcText || '';
    this.plekAtcText = this.dataset.plekAtcText || '';
    this.modalId = this.dataset.modalId || '';

    this.accepted = false;
    this._originalAtcText = null;
    this._submittedWithPlek = false;

    this.checkbox = this.querySelector('.js-rg-plek-checkbox');
    this.section = this.closest('product-form') || this.closest('[id^="main-product"]') || this.closest('section') || document;

    var formSelector = this.dataset.formSelector || '.js-product-form';
    this.form = this.section.querySelector ? this.section.querySelector(formSelector) : document.querySelector(formSelector);

    var atcSelector = this.dataset.atcSelector || '.add-to-cart';
    this.atcButton = this.form ? this.form.querySelector(atcSelector) : null;

    this.modal = this.modalId ? document.getElementById(this.modalId) : null;

    if (!this.checkbox || !this.form || !this.atcButton || !this.modal) {
      // Missing dependency; surface a console warning in dev but fail silent.
      if (window.console && console.warn) {
        console.warn('[rg-plek-upsell] Missing dependency', {
          hasCheckbox: !!this.checkbox,
          hasForm: !!this.form,
          hasAtcButton: !!this.atcButton,
          hasModal: !!this.modal
        });
      }
      return;
    }

    this._onCheckboxChange = this.onCheckboxChange.bind(this);
    this._onVariantChange = this.onVariantChange.bind(this);
    this._onFormSubmit = this.onFormSubmit.bind(this);
    this._onCartAdd = this.onCartAdd.bind(this);
    this._onAccept = this.onAccept.bind(this);
    this._onDecline = this.onDecline.bind(this);
    this._onModalAttrChange = this.onModalAttrChange.bind(this);

    this.checkbox.addEventListener('change', this._onCheckboxChange);

    this.acceptBtn = this.modal.querySelector('.js-rg-plek-accept');
    this.declineBtn = this.modal.querySelector('.js-rg-plek-decline');
    if (this.acceptBtn) this.acceptBtn.addEventListener('click', this._onAccept);
    if (this.declineBtn) this.declineBtn.addEventListener('click', this._onDecline);

    // Watch the modal's 'open' attribute so closing via backdrop/Escape
    // also unchecks the box when acceptance didn't happen.
    this._modalObserver = new MutationObserver(this._onModalAttrChange);
    this._modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });

    if (this.section && this.section.addEventListener) {
      this.section.addEventListener('on:variant:change', this._onVariantChange);
      this.section.addEventListener('on:cart:add', this._onCartAdd);
    }

    // Capture intent at submit time in case the checkbox changes mid-flight.
    this.form.addEventListener('submit', this._onFormSubmit, true);
  };

  RgPlekUpsell.prototype.disconnectedCallback = function () {
    if (this._modalObserver) {
      this._modalObserver.disconnect();
      this._modalObserver = null;
    }
  };

  RgPlekUpsell.prototype.isHidden = function () {
    return this.classList.contains('rg-plek-upsell--hidden');
  };

  RgPlekUpsell.prototype.openModal = function () {
    if (!this.modal) return;
    if (typeof this.modal.open === 'function') {
      this.modal.open(this.checkbox);
    } else {
      this.modal.setAttribute('open', '');
    }
  };

  RgPlekUpsell.prototype.closeModal = function () {
    if (!this.modal) return;
    if (typeof this.modal.close === 'function') {
      this.modal.close();
    } else {
      this.modal.removeAttribute('open');
    }
  };

  RgPlekUpsell.prototype.onCheckboxChange = function () {
    if (this.checkbox.checked) {
      if (!this.accepted) {
        this.checkbox.checked = false;
        this.openModal();
      } else {
        this.applyAtcText(true);
      }
    } else {
      // Unchecking after acceptance -- keep `accepted` true so re-checking
      // doesn't re-prompt for terms within the same page-load.
      this.applyAtcText(false);
    }
  };

  RgPlekUpsell.prototype.onAccept = function () {
    this.accepted = true;
    this.checkbox.checked = true;
    this.applyAtcText(true);
    this.closeModal();
  };

  RgPlekUpsell.prototype.onDecline = function () {
    this.checkbox.checked = false;
    this.applyAtcText(false);
    this.closeModal();
  };

  RgPlekUpsell.prototype.onModalAttrChange = function (mutations) {
    for (var i = 0; i < mutations.length; i += 1) {
      if (mutations[i].attributeName === 'open') {
        var isOpen = this.modal.hasAttribute('open');
        if (!isOpen && !this.accepted && this.checkbox.checked) {
          // Modal was closed (X button, backdrop, Escape) without acceptance;
          // reset the checkbox state.
          this.checkbox.checked = false;
          this.applyAtcText(false);
        }
      }
    }
  };

  RgPlekUpsell.prototype.applyAtcText = function (enabled) {
    if (!this.atcButton) return;
    // `data-add-to-cart-text` is what variant-picker.js writes back into the
    // button on every variant change. We update BOTH the dataset value and
    // the visible text so variant switches don't clobber our label.
    if (enabled && !this.isHidden()) {
      if (this._originalAtcText === null) {
        this._originalAtcText = this.atcButton.dataset.addToCartText || this.atcButton.textContent.trim();
      }
      this.atcButton.dataset.addToCartText = this.plekAtcText;
      if (!this.atcButton.disabled) {
        this.atcButton.textContent = this.plekAtcText;
      }
    } else if (this._originalAtcText !== null) {
      this.atcButton.dataset.addToCartText = this._originalAtcText;
      if (!this.atcButton.disabled) {
        this.atcButton.textContent = this._originalAtcText;
      }
      this._originalAtcText = null;
    }
  };

  RgPlekUpsell.prototype.onVariantChange = function (evt) {
    var variant = evt && evt.detail && evt.detail.variant;
    if (!variant || typeof variant.price !== 'number') return;

    if (variant.price >= this.priceThreshold) {
      this.hide();
    } else {
      this.show();
    }
  };

  RgPlekUpsell.prototype.hide = function () {
    this.classList.add('rg-plek-upsell--hidden');
    this.checkbox.checked = false;
    this.accepted = false;
    this.applyAtcText(false);
  };

  RgPlekUpsell.prototype.show = function () {
    this.classList.remove('rg-plek-upsell--hidden');
  };

  RgPlekUpsell.prototype.onFormSubmit = function () {
    this._submittedWithPlek = this.accepted && this.checkbox.checked && !this.isHidden();
  };

  RgPlekUpsell.prototype.onCartAdd = function () {
    if (!this._submittedWithPlek) return;
    this._submittedWithPlek = false;
    this.addPlekToCart();
  };

  RgPlekUpsell.prototype.addPlekToCart = function () {
    var self = this;

    var sectionsToUpdate = ['page-header', 'cart-drawer']
      .map(function (sel) { return document.querySelector(sel); })
      .filter(function (el) { return !!el; });

    var sectionIds = sectionsToUpdate
      .map(function (el) { return el.dataset ? el.dataset.sectionId : null; })
      .filter(function (id) { return !!id; });

    var body = new FormData();
    body.append('id', self.plekVariantId);
    body.append('quantity', '1');
    if (sectionIds.length) {
      body.append('sections', sectionIds.join(','));
      body.append('sections_url', window.location.pathname);
    }

    fetch(resolveCartAddUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: body
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, status: response.status, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          var msg = (result.data && (result.data.description || result.data.message)) || ('HTTP ' + result.status);
          throw new Error(msg);
        }

        var data = result.data;
        if (data.sections && sectionsToUpdate.length && sectionsToUpdate.length === Object.keys(data.sections).length) {
          sectionsToUpdate.forEach(function (el) {
            if (typeof el.updateFromCartChange === 'function') {
              el.updateFromCartChange(data.sections[el.dataset.sectionId]);
            }
          });
        } else {
          self.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        }
      })
      .catch(function (err) {
        var errorEl = self.form.querySelector('.js-form-error');
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = 'Guitar added to cart, but PLEK could not be added. Please add it from the cart or contact us at (404) 692-4642.';
        }
        if (window.console && console.warn) {
          console.warn('[rg-plek-upsell] Failed to add PLEK to cart', err);
        }
      });
  };

  customElements.define('rg-plek-upsell', RgPlekUpsell);
})();
